import { ref, unref, watch } from 'vue';
import { $httpFhirApi } from '@/common/api/httpFhir.service';
import { $log } from '@/common/api/log.service';
import { buildIndex, hasPurpose, locationPath, subtree } from '@/common/locationTree';

/** Matches the eMAR's own estate read: high enough that no real estate reaches it. */
const ESTATE_COUNT = 1000;

const ELEMENTS = ['Encounter.location', 'Encounter.healthcareService', 'HealthcareService.name', 'HealthcareService.location'].join(',');

/**
 * The places this patient's medications can be given.
 *
 * One per program they are enrolled in — usually one, and more only where somebody is in two
 * programs at once at different offices. That is the case this exists for: without it, which of
 * the two a dose lands at is settled by whichever the generator reaches first, which is to say by
 * nothing at all.
 *
 * Deliberately not every location in the account. A top-level Location is a geographically
 * separate place, so a flat list would offer an office in another city as readily as this
 * patient's own — and picking one would be a silent mistake nobody sees until a dose is missed.
 *
 * Each enrollment offers its own place and the administering places under it, narrowed by the
 * same medAdmin test the eMAR's site filter applies. The two lists have to agree: a site offered
 * here but absent there stamps doses onto a value no filter can select.
 *
 * @param {import('vue').Ref<string|null>|string|null} patientId
 * @returns {{sites: import('vue').Ref<Array<{id: string, name: string, label: string, enrollment: boolean}>>, loading: import('vue').Ref<boolean>, error: import('vue').Ref<Error|null>}}
 */
export function useAdministrationSites(patientId) {
    const sites = ref([]);
    const loading = ref(false);
    const error = ref(null);

    // Discards a response for a patient the screen has already moved off.
    let requestToken = 0;

    /**
     * Which location one enrollment resolves to.
     *
     * Mirrors MedPassCensus::locationFor, which is what the generator stamps a dose with: the
     * encounter's own location where the program serves it — the ordinary case, since the
     * enrollment picks the program from the location — and the program's otherwise, because a
     * program changed afterwards can leave the two disagreeing.
     */
    const locationFor = (encounter, program) => {
        const encounterLocation = encounter?.location?.[0]?.location?.id ?? encounter?.location?.id ?? null;
        const programLocations = (program?.location || []).map((reference) => reference?.id).filter(Boolean);

        if (encounterLocation && programLocations.includes(encounterLocation)) return encounterLocation;
        return programLocations[0] ?? encounterLocation;
    };

    const read = async () => {
        const token = ++requestToken;
        const id = unref(patientId);
        if (!id) {
            sites.value = [];
            return;
        }

        loading.value = true;
        error.value = null;
        try {
            const query = [
                `Encounter?type:text=PROGRAM&status=in-progress,onleave&subject=${encodeURIComponent(id)}`,
                '_include=healthcareService:HealthcareService',
                '_include=HealthcareService:location:Location',
                `_elements=${ELEMENTS}`,
                '_count=100',
            ].join('&');

            const [response, estateResponse] = await Promise.all([
                $httpFhirApi.get(query),
                // The whole active estate, because a subtree is any number of levels deep and a
                // partOf query answers one. The same read the eMAR's own site filter makes.
                $httpFhirApi.get(`Location?status=active&_count=${ESTATE_COUNT}&_elements=id,name,partOf,type`),
            ]);
            if (token !== requestToken) return;

            const rows = Array.isArray(response?.data) ? response.data : [];
            const programs = new Map(rows.filter((row) => row?.resourceType === 'HealthcareService').map((row) => [row.id, row]));

            const estate = (estateResponse?.data || []).filter((row) => row?.resourceType === 'Location');
            const index = buildIndex(estate);

            const found = new Map();
            for (const encounter of rows.filter((row) => row?.resourceType === 'Encounter')) {
                const resolved = locationFor(encounter, programs.get(encounter?.healthcareService?.id));
                if (!resolved) continue;
                // The enrollment's place and everything under it, keeping only what administers
                // medications. Two places deep in different wings carry the same name, so each is
                // offered under its path exactly as the eMAR offers it.
                for (const place of subtree(resolved, index)) {
                    if (found.has(place.id) || !hasPurpose(place, 'medAdmin')) continue;
                    found.set(place.id, {
                        id: place.id,
                        // The enrollment's own place, as opposed to somewhere finer beneath it.
                        // This is what "where the patient is" means, and so what a new schedule
                        // defaults to - the finer places are a deliberate choice, never a default.
                        enrollment: place.id === resolved,
                        name: place.name || 'Unnamed location',
                        label:
                            locationPath(place.id, index.byId)
                                .map((step) => step.name)
                                .join(' › ') ||
                            place.name ||
                            'Unnamed location',
                    });
                }
            }

            sites.value = [...found.values()].sort((a, b) => a.label.localeCompare(b.label));
        } catch (err) {
            if (token !== requestToken) return;
            error.value = err;
            // Never fails the form it sits on: an order saves without a site, and without one it
            // is given where the patient is, which is what the empty answer already means.
            sites.value = [];
            $log.warning('useAdministrationSites: could not read where this patient is enrolled', `patient ${id}`, err?.message);
        } finally {
            if (token === requestToken) loading.value = false;
        }
    };

    watch(() => unref(patientId), read, { immediate: true });

    return { sites, loading, error, refresh: read };
}
